import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  PasswordInput,
  Progress,
  Group,
  Text,
  Center,
  Box,
  Stack,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';

import { BasicButton } from '@components/Button';
import appStore from '@duck/app-store';
import { attachServerSessionToken } from '@util/axiosDefaults';
import { setSentryUserFromStore } from '@util/sentry';

function PasswordRequirement({ meets, label }) {
  return (
    <Text c={meets ? 'var(--brand-olive)' : 'var(--brand-brick)'} mt={5} size="sm">
      <Center inline>
        {meets ? (
          <IconCheck size={14} stroke={1.5} />
        ) : (
          <IconX size={14} stroke={1.5} />
        )}
        <Box ml={7}>{label}</Box>
      </Center>
    </Text>
  );
}

const requirements = [
  { re: /[0-9]/, label: 'Includes number' },
  { re: /[a-z]/, label: 'Includes lowercase letter' },
  { re: /[A-Z]/, label: 'Includes uppercase letter' },
  { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'Includes special symbol' },
];

export default function PasswordForm({
  additionalReqData,
  updating,
  onSuccess,
}) {
  const setAdmin = appStore((state) => state.setAdmin);
  const setAthlete = appStore((state) => state.setAthlete);
  const [password, setPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const inputStyles = {
    label: { color: 'var(--brand-ink)', marginBottom: 6 },
    input: {
      background: 'var(--brand-surface-muted)',
      color: 'var(--brand-ink)',
      border: '1px solid var(--brand-sand)',
    },
    visibilityToggle: { color: 'var(--brand-ink-muted)' },
  };

  function applySession(session) {
    if (!session?.server_session_token) return;
    attachServerSessionToken(session.server_session_token);
    if (session.admin_id) {
      setAdmin(session);
    } else if (session.athlete_id) {
      setAthlete(session);
    }
    setSentryUserFromStore();
  }

  function onSubmit(e) {
    e.preventDefault();
    setLoading(true);

    const req = {
      ...additionalReqData,
      password,
      confirmed_password: confirmedPassword,
    };

    if (currentPassword) {
      req.current_password = currentPassword;
    }

    axios
      .post('/password/', req)
      .then(({ data }) => {
        setLoading(false);
        toast.success('Password saved');
        const session = data.response && data.response[0];
        if (session) {
          applySession(session);
        }
        if (updating) {
          setPassword('');
          setConfirmedPassword('');
          setCurrentPassword('');
        }
        if (onSuccess) {
          onSuccess(session);
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const strength = getStrength(password);
  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(password)}
    />
  ));

  const bars = Array(4)
    .fill(0)
    .map((_, index) => (
      <Progress
        styles={{ section: { transitionDuration: '0ms' } }}
        value={
          password.length > 0 && index === 0
            ? 100
            : strength >= ((index + 1) / 4) * 100
              ? 100
              : 0
        }
        color={strength > 80 ? 'teal' : strength > 50 ? 'yellow' : 'red'}
        key={index}
        size={4}
      />
    ));

  return (
    <form onSubmit={onSubmit}>
      <Stack gap="sm">
        {updating && (
          <PasswordInput
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            label="Current password"
            styles={inputStyles}
          />
        )}
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          label="New password"
          styles={inputStyles}
        />
        <PasswordInput
          value={confirmedPassword}
          onChange={(e) => setConfirmedPassword(e.target.value)}
          label="New password, again"
          styles={inputStyles}
        />
        <Group gap={5} mt="xs" mb="md" grow>
          {bars}
        </Group>
        <PasswordRequirement
          label="Has at least 6 characters"
          meets={password.length > 5}
        />
        {checks}
        <BasicButton
          text="Save"
          onClick={onSubmit}
          loading={loading}
          disabled={
            loading ||
            !password ||
            !confirmedPassword ||
            password !== confirmedPassword ||
            (updating && !currentPassword) ||
            strength < 50
          }
          variant="filled"
          size="md"
          style={{
            background: 'var(--brand-brick)',
            borderColor: 'var(--brand-brick)',
            marginTop: '1em',
          }}
          primary
        />
      </Stack>
    </form>
  );
}

PasswordForm.defaultProps = {
  additionalReqData: {},
  updating: false,
};

function getStrength(password) {
  let multiplier = password.length > 5 ? 0 : 1;

  requirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 0);
}
